import React, { useState } from 'react';
import { Send, X, Lock, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { submitStudyDraftForApproval } from '../../services/reviewWorkflowApi';

/**
 * "Submit Design for Approval" modal (FECOA-1601).
 *
 * Locks the design from editing once submitted; the submitter can add an
 * optional message to the reviewer, which is stored on the review submission and
 * surfaced to the approver. When the draft was previously denied, the latest
 * reviewer feedback is shown so the submitter can confirm they addressed it.
 *
 * Props:
 *   open           - whether the modal is shown
 *   onClose        - close handler
 *   draftId        - StudyDraft id to submit
 *   draftName      - display name for the study card
 *   denialFeedback - optional latest denial reason (shows the "previously denied" banner)
 *   onSubmitted    - called after a successful submit
 */
const SubmitForApprovalModal = ({ open, onClose, draftId, draftName, denialFeedback, onSubmitted }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!draftId) return;
    setBusy(true);
    try {
      await submitStudyDraftForApproval(draftId, note.trim());
      toast.success('Submitted for approval.');
      onSubmitted && onSubmitted();
    } catch (err) {
      const data = err?.response?.data;
      const detail = Array.isArray(data?.errors) && data.errors.length
        ? `${data.detail || 'This study cannot be submitted yet.'} ${data.errors.join(' ')}`
        : (data?.detail || data?.reason || 'Could not submit for approval.');
      toast.error(detail);
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-[10000] p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-primary-600 dark:bg-primary-700">
          <div className="flex items-center gap-2 text-white">
            <Send className="w-5 h-5" />
            <h3 className="text-lg font-semibold">Submit Design for Approval</h3>
          </div>
          <button
            onClick={() => !busy && onClose()}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Study card */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
            <div className="font-semibold text-gray-900 dark:text-gray-100">{draftName}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Study Design Configuration</div>
          </div>

          {/* Previously-denied feedback */}
          {denialFeedback && (
            <div className="rounded-xl border border-red-200 dark:border-red-800/60 bg-red-50 dark:bg-red-900/20 px-4 py-3">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-semibold text-sm">
                <AlertCircle className="w-4 h-4" />
                Previously denied — Reviewer feedback
              </div>
              <p className="mt-1 text-sm text-red-700/90 dark:text-red-200/90 italic">“{denialFeedback}”</p>
            </div>
          )}

          {/* Lock warning */}
          <div className="rounded-xl border border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 flex gap-3">
            <Lock className="w-5 h-5 text-blue-600 dark:text-blue-300 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Once submitted, this study design will be <strong>locked from editing</strong> until
              an admin approves or denies the submission. You can withdraw at any time to resume editing.
            </p>
          </div>

          {/* Message to reviewer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Message to reviewer <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder="e.g. All schedule configurations are complete and verified against protocol v2.1. Please pay attention to Stage 2 frequency settings."
              className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex items-center gap-3">
          <button
            onClick={handleSubmit}
            disabled={busy}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            <Send className="w-4 h-4" />
            {busy ? 'Submitting…' : 'Submit for Approval'}
          </button>
          <button
            onClick={() => !busy && onClose()}
            disabled={busy}
            className="px-5 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmitForApprovalModal;
